import { Injectable } from "@angular/core";
import { Observable, of, delay, tap } from "rxjs";
import { AuthService } from "./auth.service";
import { AuthResponse, LoginCredentials } from "../interfaces/auth.interface";
import { User } from "../interfaces/user.interface";
import { UserStats } from "../interfaces/user-stats.interface";
import {
  RegistrationRequest,
  RegistrationResponse,
} from "../interfaces/registration-request.interface";

@Injectable({
  providedIn: "root",
})
export class AuthMockService extends AuthService {
  constructor() {
    super();
  }

  login(credentials: LoginCredentials): Observable<AuthResponse> {
    const mockResponse: AuthResponse = {
      jwtToken: "mock-jwt-token-" + Math.random().toString(36).substring(7),
    };

    // Use wrapRequest to handle loading state and error toasting
    return this.wrapRequest(
      of(mockResponse)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Signed in successfully");
          }),
        ),
      true, // Show success message
      "Login failed", // Custom error message if needed
    );
  }

  logout(): Observable<void> {
    console.log("Mock Logout triggered");
    this.toast.success("Logged out successfully");
    return of(undefined);
  }

  forgotPassword(email: string): Observable<void> {
    return this.wrapRequest(
      of(undefined)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Password reset email sent successfully");
          }),
        ),
      false, // Show success message
      "Forgot password failed", // Custom error message if needed
    );
  }

  verifyOtp(
    uniqueId: string,
    contactType: string,
    otpCode: string,
  ): Observable<void> {
    return this.wrapRequest(
      of(undefined)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("OTP verified successfully");
          }),
        ),
      false, // Show success message
      "Verify OTP failed", // Custom error message if needed
    );
  }

  resetPassword(token: string, newPassword: string): Observable<void> {
    return this.wrapRequest(
      of(undefined)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Password reset successfully");
          }),
        ),
      false, // Show success message
      "Reset password failed", // Custom error message if needed
    );
  }

  currentUser(): Observable<User> {
    return this.wrapRequest(
      of({
        uniqueId: "1",
        fullName: "John Doe",
        email: "john.doe@example.com",
        isNewUser: true,
        isProfileCompleted: false,
        isVerified: true,
      })
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("User details fetched successfully");
          }),
        ),
      false, // Show success message
      "Fetch user details failed", // Custom error message if needed
    );
  }

  currentUserStats(): Observable<UserStats> {
    return this.wrapRequest(
      of<UserStats>({
        verifiedStatus: {
          isEmailVerified: true,
          isPhoneVerified: false,
          termsAgreed: false,
          productUpdates: false,
          communityUpdates: false,
        },
        status: "Active",
        totalInstitutions: 1,
      })
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("User stats fetched successfully");
          }),
        ),
      false, // Show success message
      "Fetch user stats failed", // Custom error message if needed
    );
  }

  verifyEmail(otp: string): Observable<void> {
    return this.wrapRequest(
      of(undefined)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Email verified successfully");
          }),
        ),
      true,
    );
  }

  resendEmailOtp(): Observable<void> {
    return this.wrapRequest(
      of(undefined)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Verification code resent to email");
          }),
        ),
      true,
    );
  }

  updateEmail(email: string): Observable<void> {
    return this.wrapRequest(
      of(undefined)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Email updated successfully");
          }),
        ),
      true,
    );
  }

  verifyPhone(uniqueId: string, otp: string): Observable<void> {
    return this.wrapRequest(
      of(undefined)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Phone verified successfully");
          }),
        ),
      true,
    );
  }

  resendPhoneOtp(params: {
    uniqueId: string;
    contactType: string;
  }): Observable<boolean> {
    return this.wrapRequest(
      of(true)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Verification code resent to phone");
          }),
        ),
      true,
    );
  }

  updatePhone(phone: string): Observable<string> {
    return this.wrapRequest(
      of("mock-uid-" + Math.random().toString(36).substring(7))
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Phone number updated successfully");
          }),
        ),
      true,
    );
  }

  acceptTerms(data: {
    isAgreed: boolean;
    isSubscribeTips: boolean;
    isSubscribeCommunity: boolean;
  }): Observable<void> {
    return this.wrapRequest(
      of(undefined)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Preferences saved successfully");
          }),
        ),
      true,
    );
  }

  signup(
    registrationRequest: RegistrationRequest,
  ): Observable<RegistrationResponse> {
    const mockResponse: RegistrationResponse = {
      uniqueId: "mock-unique-id-" + Math.random().toString(36).substring(7),
      fullName: registrationRequest.fullName,
      email: registrationRequest.email,
      phone: "", // initialize with empty string since it's no longer provided at signup
      isEmailVerified: false,
      createdBy: "system",
      createdDateTime: new Date().toISOString(),
      updatedBy: "system",
      updatedDateTime: new Date().toISOString(),
      isActive: true,
    };

    return this.wrapRequest(
      of(mockResponse)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("User registered successfully");
          }),
        ),
      true,
    );
  }

  updateRegistrationEmail(
    uniqueId: string,
    email: string,
  ): Observable<boolean> {
    return this.wrapRequest(
      of(true)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Email updated successfully");
          }),
        ),
      true,
    );
  }

  updateRegistrationPhone(
    uniqueId: string,
    phone: string,
  ): Observable<boolean> {
    return this.wrapRequest(
      of(true)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Phone number updated successfully");
          }),
        ),
      true,
    );
  }

  resendRegistrationEmailOtp(uniqueId: string): Observable<boolean> {
    return this.wrapRequest(
      of(true)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Verification code resent to email");
          }),
        ),
      true,
    );
  }

  resendRegistrationPhoneOtp(uniqueId: string): Observable<boolean> {
    return this.wrapRequest(
      of(true)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Verification code resent to phone");
          }),
        ),
      true,
    );
  }

  verifyRegistrationEmailOtp(
    uniqueId: string,
    otp: string,
  ): Observable<boolean> {
    return this.wrapRequest(
      of(true)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Email verified successfully");
          }),
        ),
      true,
    );
  }

  verifyRegistrationPhoneOtp(
    uniqueId: string,
    otp: string,
  ): Observable<boolean> {
    return this.wrapRequest(
      of(true)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Phone verified successfully");
          }),
        ),
      true,
    );
  }

  acceptRegistrationTerms(
    uniqueId: string,
    data: {
      isAgreed: boolean;
      isSubscribeTips: boolean;
      isSubscribeCommunity: boolean;
    },
  ): Observable<RegistrationResponse> {
    return this.wrapRequest(
      of<RegistrationResponse>({
        uniqueId,
        jwtToken: "mock-jwt-token-" + Math.random().toString(36).substring(7),
        isEmailVerified: true,
        createdBy: "system",
        createdDateTime: new Date().toISOString(),
        updatedBy: "system",
        updatedDateTime: new Date().toISOString(),
        isActive: true,
      })
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Terms accepted successfully");
          }),
        ),
      true,
    );
  }

  updateFcmToken(token: string): Observable<any> {
    return this.wrapRequest(
      of({ success: true })
        .pipe(delay(1000))
        .pipe(
          tap(() => {
            console.log("FCM token updated successfully:", token);
          }),
        ),
      false,
    );
  }

  getInviteDetails(inviteId: string): Observable<RegistrationRequest> {
    return this.wrapRequest(
      of({
        uniqueId: inviteId,
        fullName: "John Doe",
        email: "john.doe@example.com",
      })
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Invite details fetched successfully");
          }),
        ),
      false,
    );
  }

  signInByRegistration(uniqueId: string): Observable<AuthResponse> {
    const mockResponse: AuthResponse = {
      jwtToken: "mock-jwt-token-reg-" + Math.random().toString(36).substring(7),
    };

    return this.wrapRequest(
      of(mockResponse)
        .pipe(delay(1500))
        .pipe(
          tap(() => {
            this.toast.success("Signed in by registration successfully");
          }),
        ),
      true,
    );
  }
}
